import React, { useState, useEffect } from "react";
import { auth } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { jsPDF } from "jspdf";
import { motion } from "framer-motion";

import categories from "./data/items";
import Header from "./header";
import DailyReport from "./DailyReport";

function App() {
  // USER & AUTH
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [authError, setAuthError] = useState("");

  // CART & ORDERS
  const [cart, setCart] = useState([]);
  const [team, setTeam] = useState("");
  const [location, setLocation] = useState("");
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [showOrderPopup, setShowOrderPopup] = useState(false);

  // DAILY REPORT
  const [showDailyReport, setShowDailyReport] = useState(false);

  // primer podataka za dnevni izveštaj
  const dailyReportData = [
    { code: "A101", name: "Screw", quantity: 12 },
    { code: "B202", name: "Bolt", quantity: 5 },
    { code: "C303", name: "Washer", quantity: 20 },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      if (isRegister) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      setShowLoginModal(false);
      setEmail("");
      setPassword("");
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const addToCart = (item) => {
    const exists = cart.find((i) => i.id === item.id);
    if (exists) {
      setCart(
        cart.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const increaseQuantity = (id) => {
    setCart(
      cart.map((i) => (i.id === id ? { ...i, quantity: i.quantity + 1 } : i))
    );
  };

  const decreaseQuantity = (id) => {
    setCart(
      cart
        .map((i) => (i.id === id ? { ...i, quantity: i.quantity - 1 } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const copyToClipboard = () => {
    const text = `Order For Team ${team}\nLocation: ${location}\n\nItems:\n${cart
      .map((item) => `${item.code} — ${item.name} (x${item.quantity})`)
      .join("\n")}`;
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text(`Order For Team ${team}`, 10, 10);
    doc.text(`Location: ${location}`, 10, 20);
    doc.text("Items:", 10, 30);
    cart.forEach((item, index) => {
      doc.text(
        `${item.code} — ${item.name} (x${item.quantity})`,
        10,
        40 + index * 10
      );
    });
    doc.save(`order_team${team}.pdf`);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* HEADER */}
      <Header
        user={user}
        onLogout={handleLogout}
        onOpenReport={() => setShowDailyReport(true)}
      />

      {/* HERO */}
      <section
        className="relative h-[70vh] md:h-screen bg-cover bg-center flex flex-col justify-center items-center text-white"
        style={{ backgroundImage: "url('/images/bghero.png')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-transparent"></div>

        <div className="relative z-10 text-center px-4">
          <motion.img
            src="/images/logodemo.png"
            alt="Logo"
            className="mx-auto mb-6 w-32 h-32 md:w-64 md:h-64 object-contain drop-shadow-xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
          <motion.h2
            className="text-3xl md:text-5xl font-extrabold mb-4 drop-shadow-lg"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            Virtual Storage v1.2
          </motion.h2>

          <motion.button
            onClick={() => setShowLoginModal(true)}
            className="px-6 py-3 bg-blue-500 text-white rounded-full font-semibold shadow-lg hover:bg-blue-600 transition z-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            Log In
          </motion.button>
        </div>

        <footer className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-center py-2">
          Powered by UZ
        </footer>
      </section>

      {/* CATALOG & CART */}
      <div className="flex flex-col md:flex-row flex-1">
        {/* Catalog */}
        <div className="w-full md:w-3/4 p-6">
          <h1 className="text-3xl font-bold mb-4">Items</h1>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded ${
                  activeCategory === cat.id
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Items grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {categories
              .find((c) => c.id === activeCategory)
              .items.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-4 flex flex-col items-center shadow hover:shadow-lg transition-shadow"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 md:w-24 md:h-24 object-contain mb-2 transform transition-transform hover:scale-105"
                  />
                  <h3 className="font-semibold text-center">{item.name}</h3>
                  <p className="text-gray-500 text-sm">Code: {item.code}</p>
                  <button
                    onClick={() => addToCart(item)}
                    className="mt-2 bg-blue-500 text-white px-4 py-1 rounded"
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
          </div>
        </div>

        {/* Cart */}
        <div className="w-full md:w-1/4 bg-gray-50 shadow-xl p-6 flex flex-col">
          <h2 className="text-xl font-bold mb-6 border-b pb-2">🛒 Cart</h2>
          {cart.length === 0 ? (
            <p className="text-gray-500 text-center mt-10">Place order.</p>
          ) : (
            <div className="flex-1 space-y-4 overflow-y-auto">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="bg-white p-4 rounded-xl shadow flex items-center justify-between"
                >
                  <div className="flex flex-col">
                    <span className="font-semibold">{item.name}</span>
                    <span className="text-sm text-gray-500">
                      {item.code} — x{item.quantity}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => decreaseQuantity(item.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded-full hover:bg-red-600"
                    >
                      -
                    </button>
                    <button
                      onClick={() => increaseQuantity(item.id)}
                      className="bg-green-500 text-white px-3 py-1 rounded-full hover:bg-green-600"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {cart.length > 0 && (
            <div className="border-t mt-6 pt-4">
              <button
                onClick={() => setShowOrderPopup(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-xl w-full hover:bg-blue-700 shadow"
              >
                Place an order
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Order popup */}
      {showOrderPopup && (
        <div className="fixed inset-0 bg-blue-100/50 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Number
              </label>
              <input
                type="number"
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                className="w-full border rounded p-2"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border rounded p-2"
              />
            </div>

            <div className="mb-4">
              <h3 className="font-semibold">Items:</h3>
              <ul className="list-disc list-inside">
                {cart.map((item) => (
                  <li key={item.id}>
                    {item.code} — {item.name} (x{item.quantity})
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-2 justify-between">
              <button
                onClick={copyToClipboard}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Copy
              </button>
              <button
                onClick={downloadPDF}
                className="bg-green-500 text-white px-4 py-2 rounded"
              >
                Download PDF
              </button>
              <button
                onClick={() => setShowOrderPopup(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOGIN MODAL */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-blue-100/50 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {isRegister ? "Register" : "Team Login"}
            </h2>
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border rounded p-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border rounded p-2"
                />
              </div>
              {authError && <p className="text-red-600 mb-3">{authError}</p>}
              <div className="flex flex-wrap gap-2 justify-between">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  {isRegister ? "Register" : "Login"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowLoginModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Close
                </button>
              </div>
            </form>
            <p
              className="mt-4 text-blue-600 cursor-pointer hover:underline"
              onClick={() => setIsRegister(!isRegister)}
            >
              {isRegister
                ? "Already have an account? Login"
                : "Don't have an account? Register"}
            </p>
          </div>
        </div>
      )}

      {/* DAILY REPORT POPUP */}
      {showDailyReport && (
        <DailyReport
          onClose={() => setShowDailyReport(false)}
          data={dailyReportData}
        />
      )}
    </div>
  );
}

export default App;
